import {skipToken} from '@reduxjs/toolkit/query';
import {Card, Form, FormField, FormSubmitHandler, showNotification} from 'platform/components';
import {Show, Space, VStack} from 'platform/foundation';

import {useParams} from 'react-router-dom';

import {isNil, isNotNil} from 'ramda';

import {
  useCreateTelephonyGroupMutation,
  useGetTelephonyGroupQuery,
  useUpdateTelephonyGroupMutation,
} from '@dms/api/settings';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsFooter, SettingsSection, SettingsTemplate} from '@dms/settings/shared';
import {handleApiError, usePermissions} from '@dms/shared';

import {ApiError, useNavigate} from 'shared/web';

import {TelephonyGroupMembersCard} from './components/TelephonyGroupMembersCard';
import {TelephonyGroupRingStrategyCard} from './components/TelephonyGroupRingStrategyCard';
import {
  EMPTY_TELEPHONY_GROUP_FORM_VALUES,
  getTelephonyGroupFormValues,
  getTelephonyGroupRequestBody,
  telephonyGroupFormSchema,
  TelephonyGroupFormValues,
} from './utils/telephonyGroupFormSchema';

export function TelephonyGroupDetail() {
  const {groupId} = useParams();
  const navigate = useNavigate();

  const isCreating = isNil(groupId);

  const {
    data: group,
    isLoading,
    isError,
  } = useGetTelephonyGroupQuery(isNotNil(groupId) ? {groupId} : skipToken);

  const [createGroup] = useCreateTelephonyGroupMutation();
  const [updateGroup] = useUpdateTelephonyGroupMutation();

  const [canWriteTelephonySettings] = usePermissions({
    permissionKeys: ['telephonySettingsWrite'],
  });

  const isReadOnly = !canWriteTelephonySettings;

  const handleSubmit: FormSubmitHandler<TelephonyGroupFormValues> = async (
    values,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    const body = getTelephonyGroupRequestBody(values);

    const request = isNil(groupId)
      ? createGroup({body}).unwrap()
      : updateGroup({groupId, body}).unwrap();

    await request
      .then(() => {
        showNotification.success(i18n.t('general.notifications.changesSuccessfullySaved'));

        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }

        navigate(settingsRoutes.telephonyGroups);
      })
      .catch((error: ApiError) =>
        handleApiError(error, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  const defaultValues = isNotNil(group)
    ? getTelephonyGroupFormValues(group)
    : EMPTY_TELEPHONY_GROUP_FORM_VALUES;

  return (
    <SettingsTemplate
      header={{
        title: isCreating ? i18n.t('page.settings.telephony.newGroup') : group?.name,
        breadcrumbs: [
          {label: i18n.t('page.settings.telephony.title')},
          {
            label: i18n.t('page.settings.telephony.groupsTitle'),
            href: settingsRoutes.telephonyGroups,
          },
        ],
      }}
      isLoading={isLoading}
      isError={isError}
      isCreating={isCreating}
      data-testid={testIds.settings.telephonyGroupDetail('settingsTemplate')}
    >
      <Show when={isCreating || isNotNil(group)}>
        <SettingsSection>
          <Form<TelephonyGroupFormValues>
            // Remounts the form on a parameter-only navigation, where defaultValues alone
            // would keep the previously loaded group's values.
            key={group?.id ?? 'new'}
            experimentalZodSchema={telephonyGroupFormSchema}
            onSubmit={handleSubmit}
            defaultValues={defaultValues}
            shouldWatchForUnsavedChanges
          >
            {(control, formApi, navigateWithoutBlocker) => (
              <VStack spacing={4}>
                <Card title={i18n.t('page.settings.telephony.groupCardTitle')}>
                  <FormField
                    control={control}
                    name="name"
                    type="text"
                    label={i18n.t('general.labels.name')}
                    isRequired
                    isDisabled={isReadOnly}
                    data-testid={testIds.settings.telephonyGroupDetail('name')}
                  />
                </Card>
                <TelephonyGroupRingStrategyCard
                  control={control}
                  isReadOnly={isReadOnly}
                  data-testid={testIds.settings.telephonyGroupDetail('ringStrategy')}
                />
                <TelephonyGroupMembersCard
                  control={control}
                  formApi={formApi}
                  isReadOnly={isReadOnly}
                  data-testid={testIds.settings.telephonyGroupDetail('members')}
                />
                <Show when={canWriteTelephonySettings}>
                  <Space vertical={18} />
                  <SettingsFooter
                    actions={[
                      {
                        type: 'button',
                        title: i18n.t('general.actions.discardChanges'),
                        variant: 'secondary',
                        onClick: () => navigateWithoutBlocker(settingsRoutes.telephonyGroups),
                        'data-testid': testIds.settings.telephonyGroupDetail('discard'),
                      },
                      {
                        type: 'form-button',
                        control,
                        title: isCreating
                          ? i18n.t('general.actions.create')
                          : i18n.t('general.actions.saveChanges'),
                        buttonType: 'submit',
                        variant: 'primary',
                        'data-testid': testIds.settings.telephonyGroupDetail('save'),
                      },
                    ]}
                  />
                </Show>
              </VStack>
            )}
          </Form>
        </SettingsSection>
      </Show>
    </SettingsTemplate>
  );
}
